// ProfileSetupPage — first-run onboarding: collect the student_profiles
// fields required before the dashboard unlocks (see checkProfileComplete on
// the API). Rendered inside AuthLayout's full-screen centered-card shell —
// deliberately NOT StudentLayout, so no sidebar renders here.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { PreferredPaymentMethod, Profession } from "@dm-lms/shared/types";
import { KENYA_COUNTRY_CODE, PERSON_NAME_MIN_LENGTH, PERSON_NAME_MAX_LENGTH } from "@dm-lms/shared/constants";
import { isStudentProfileComplete } from "@dm-lms/shared/utils";
import { studentPhoneSchema } from "@dm-lms/shared/validations";
import { phoneError } from "../../lib/phoneError.js";
import { useAuth } from "../../hooks/useAuth.js";
import { patchJson } from "../../hooks/useApi.js";
import { PAYMENT_OPTIONS, PROFESSION_OPTIONS, ORGANISATION_HINT } from "../../lib/profileFormOptions.js";
import LocationFields from "../../components/student/LocationFields.js";

const inputClass =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";

export default function ProfileSetupPage() {
  const navigate = useNavigate();
  const { user, refetch } = useAuth();

  const [name, setName] = useState(user?.name ?? "");
  const [phone, setPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [idNumber, setIdNumber] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PreferredPaymentMethod | null>(null);
  const [mpesaNumber, setMpesaNumber] = useState("");
  const [organization, setOrganization] = useState("");
  const [profession, setProfession] = useState<Profession | "">("");
  const [country, setCountry] = useState<string>(KENYA_COUNTRY_CODE);
  const [county, setCounty] = useState("");
  const [region, setRegion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const phoneProblem = phoneError(studentPhoneSchema, phone);
  const trimmedName = name.trim();
  const nameValid = trimmedName.length >= PERSON_NAME_MIN_LENGTH && trimmedName.length <= PERSON_NAME_MAX_LENGTH;
  const canSubmit = nameValid && phoneProblem === null && isStudentProfileComplete({
    phone,
    idNumber,
    organization,
    profession,
    country,
    county,
    region,
    preferredPaymentMethod: paymentMethod,
  });

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit || isSubmitting) return;

    setError(null);
    setIsSubmitting(true);

    const res = await patchJson("/api/profile/student", {
      name: trimmedName,
      phone: phone.trim(),
      id_number: idNumber,
      preferred_payment_method: paymentMethod,
      ...(paymentMethod === "mpesa" && mpesaNumber.trim() !== "" ? { mpesa_number: mpesaNumber } : {}),
      organization,
      profession,
      country,
      ...(country === KENYA_COUNTRY_CODE ? { county } : { region }),
    });

    if (!res.ok) {
      setError(res.error);
      setIsSubmitting(false);
      return;
    }

    await refetch();
    navigate("/app/student", { replace: true });
  }

  return (
    <div>
      <h1 className="font-display text-xl font-semibold text-dm-navy text-center">Complete your profile</h1>
      <p className="mt-2 text-sm text-dm-slate text-center">
        Fill in a few details before you get started.
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-5">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-dm-navy">
            Full name
          </label>
          <input
            id="name"
            type="text"
            required
            minLength={PERSON_NAME_MIN_LENGTH}
            maxLength={PERSON_NAME_MAX_LENGTH}
            autoComplete="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-dm-navy">
            Phone number
          </label>
          <input
            id="phone"
            type="tel"
            required
            placeholder="+254700000000"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            onBlur={() => setPhoneTouched(true)}
            aria-invalid={phoneTouched && phoneProblem !== null}
            aria-describedby={phoneTouched && phoneProblem !== null ? "phone-error" : undefined}
            className={inputClass}
          />
          {phoneTouched && phoneProblem !== null && (
            <p id="phone-error" role="alert" className="mt-1 text-xs text-red-600">{phoneProblem}</p>
          )}
        </div>

        <div>
          <label htmlFor="idNumber" className="block text-sm font-medium text-dm-navy">
            National ID / Passport
          </label>
          <input
            id="idNumber"
            type="text"
            required
            placeholder="12345678"
            value={idNumber}
            onChange={(event) => setIdNumber(event.target.value)}
            className={inputClass}
          />
          <p className="mt-1 text-xs text-dm-slate">
            Required for certificate issuance. Must match your government ID.
          </p>
        </div>

        <div>
          <span className="block text-sm font-medium text-dm-navy mb-2">Payment preference</span>
          <div className="grid grid-cols-2 gap-3">
            {PAYMENT_OPTIONS.map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                aria-pressed={paymentMethod === value}
                onClick={() => setPaymentMethod(value)}
                className={`flex flex-col items-center gap-2 rounded-lg border-2 px-4 py-4 text-sm transition ${
                  paymentMethod === value
                    ? "border-dm-royal bg-dm-royal/5 text-dm-navy"
                    : "border-gray-200 text-dm-slate hover:border-gray-300"
                }`}
              >
                <Icon width={24} height={24} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {paymentMethod === "mpesa" && (
          <div>
            <label htmlFor="mpesaNumber" className="block text-sm font-medium text-dm-navy">
              M-Pesa number
            </label>
            <input
              id="mpesaNumber"
              type="tel"
              placeholder="Leave blank to use your phone number above"
              value={mpesaNumber}
              onChange={(event) => setMpesaNumber(event.target.value)}
              className={inputClass}
            />
          </div>
        )}

        <div>
          <label htmlFor="organization" className="block text-sm font-medium text-dm-navy">
            Organisation
          </label>
          <input
            id="organization"
            type="text"
            required
            value={organization}
            onChange={(event) => setOrganization(event.target.value)}
            className={inputClass}
          />
          <p className="mt-1 text-xs text-dm-slate">{ORGANISATION_HINT}</p>
        </div>

        <div>
          <label htmlFor="profession" className="block text-sm font-medium text-dm-navy">
            Profession
          </label>
          <select
            id="profession"
            required
            value={profession}
            onChange={(event) => setProfession(event.target.value as Profession | "")}
            className={inputClass}
          >
            <option value="">Select…</option>
            {PROFESSION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <LocationFields
          country={country}
          county={county}
          region={region}
          onCountry={setCountry}
          onCounty={setCounty}
          onRegion={setRegion}
          inputClass={inputClass}
        />

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={!canSubmit || isSubmitting}
          className="w-full rounded-md bg-dm-navy px-4 py-3 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? "Saving…" : "Continue to dashboard"}
        </button>
      </form>
    </div>
  );
}
